import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { LinkSimple, Plus, X } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { useAuthStore } from '../../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import {
  ThirdPartyIntegrationService,
  type ConnectionMode,
} from '../../../service/ThirdPartyIntegrationService';
import ConnectToProvider from '../oauth/ConnectToProvider';
import CredentialsForm from './CredentialsForm';
import type { IntegrationProviderConfig, ProviderIconComponent } from '../types';

interface ConnectionStatusProps {
  provider: string;
  config?: IntegrationProviderConfig | null;
  onReconnect?: () => void;
  onConnectionSelected?: (integrationId: string, name: string) => void;
  onDisconnected?: () => void;
  selectedIntegrationId?: string;
  currentUserId?: string;
}

function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

interface ProviderIconBadgeProps {
  IconComponent: ProviderIconComponent | null;
  brandColor: string;
  size?: number;
  isDark: boolean;
}

const ProviderIconBadge: React.FC<ProviderIconBadgeProps> = ({
  IconComponent,
  brandColor,
  size = 32,
  isDark,
}) => {
  const rgb = hexToRgb(brandColor);
  const bgAlpha = isDark ? '0.15' : '0.10';

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '6px',
        backgroundColor: `rgba(${rgb}, ${bgAlpha})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {IconComponent ? (
        <IconComponent size={size * 0.55} color={brandColor} />
      ) : (
        <LinkSimple size={size * 0.5} weight="bold" color={brandColor} />
      )}
    </Box>
  );
};

const NEW_CONNECTION_VALUE = '__new__';

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  provider,
  config,
  onReconnect,
  onConnectionSelected,
  onDisconnected,
  selectedIntegrationId,
  currentUserId,
}) => {
  const { user } = useAuthStore((state) => state);
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  const { fetchConnections, getConnections, removeConnection } =
    useStoreThirdPartyIntegration();

  const connections = useStoreThirdPartyIntegration((s) =>
    s.getConnections(provider),
  );
  const hasAnyConnection = connections.length > 0;

  const [connectionMode, setConnectionMode] = useState<ConnectionMode | null>(
    null,
  );
  const [loadingMode, setLoadingMode] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConnectingWebhook, setIsConnectingWebhook] = useState(false);

  const isDark = user?.theme === 'dark';
  const providerName = config?.name ?? provider;

  const rawIcon = config?.icon;
  const IconComponent: ProviderIconComponent | null =
    rawIcon && typeof rawIcon !== 'string'
      ? (rawIcon as ProviderIconComponent)
      : null;

  const defaultAccent = isDark ? '#7F56D9' : '#6941C6';
  const brandColor = config?.brandColor ?? defaultAccent;

  const selectedConnection = selectedIntegrationId
    ? connections.find((c) => c.id === selectedIntegrationId)
    : undefined;

  const isOwnConnection =
    selectedConnection?.isOwn ??
    (selectedConnection ? selectedConnection.ownerUserId === currentUserId : false);

  useEffect(() => {
    if (provider) {
      fetchConnections(provider);
    }
  }, [provider, fetchConnections]);

  useEffect(() => {
    if (!provider || hasAnyConnection) {
      setConnectionMode(null);
      return;
    }
    let cancelled = false;
    setLoadingMode(true);
    ThirdPartyIntegrationService.getConnectionMode(provider)
      .then((res) => {
        if (!cancelled && res?.data?.connectionMode) {
          setConnectionMode(res.data.connectionMode as ConnectionMode);
        } else {
          setConnectionMode(null);
        }
      })
      .catch(() => {
        if (!cancelled) setConnectionMode(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingMode(false);
      });
    return () => {
      cancelled = true;
    };
  }, [provider, hasAnyConnection]);

  const handleDisconnect = useCallback(async () => {
    if (!selectedIntegrationId) return;
    setIsDisconnecting(true);
    try {
      await ThirdPartyIntegrationService.disconnect(
        provider,
        selectedIntegrationId,
      );
      removeConnection(provider, selectedIntegrationId);
      onDisconnected?.();
      setConfirmOpen(false);
      setNotification({
        type: NotificationType.Success,
        message: `${providerName} disconnected.`,
      });
    } catch (err) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(err),
      });
    } finally {
      setIsDisconnecting(false);
    }
  }, [
    provider,
    providerName,
    selectedIntegrationId,
    removeConnection,
    setNotification,
    onDisconnected,
  ]);

  const handleNewConnection = useCallback(
    (integrationId: string, name: string) => {
      fetchConnections(provider);
      onConnectionSelected?.(integrationId, name);
      setShowNewForm(false);
    },
    [provider, fetchConnections, onConnectionSelected],
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === NEW_CONNECTION_VALUE) {
        setShowNewForm(true);
        return;
      }
      const conn = connections.find((c) => c.id === value);
      if (conn) {
        onConnectionSelected?.(conn.id, conn.name);
      }
    },
    [connections, onConnectionSelected],
  );

  const handleWebhookConnect = useCallback(async () => {
    setIsConnectingWebhook(true);
    try {
      const res = await ThirdPartyIntegrationService.connect(provider, {});
      const data = res?.data as { integrationId?: string; name?: string } | undefined;
      const id = data?.integrationId;
      const name = data?.name ?? providerName;
      if (id) {
        await fetchConnections(provider);
        onConnectionSelected?.(id, name);
        setNotification({
          type: NotificationType.Success,
          message: `Connected to ${providerName}. Use the Webhook URL in your Zap.`,
        });
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(new Error('No integration ID returned')),
        });
      }
    } catch (err) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(err),
      });
    } finally {
      setIsConnectingWebhook(false);
    }
  }, [provider, providerName, fetchConnections, onConnectionSelected, setNotification]);

  const cardBorder = isDark
    ? '1px solid rgba(71, 84, 103, 0.5)'
    : '1px solid #D0D5DD';
  const cardBg = isDark ? 'rgba(16, 24, 40, 0.45)' : '#F9FAFB';
  const titleColor = isDark ? '#F9FAFB' : '#344054';
  const subtitleColor = isDark ? '#98A2B3' : '#667085';

  if (showNewForm) {
    const CustomCard = config?.ConnectionCard;

    if (CustomCard) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <CustomCard
            provider={provider}
            isConnected={false}
            onConnected={handleNewConnection}
          />
          <Button
            className="nodrag"
            size="small"
            onClick={() => setShowNewForm(false)}
            sx={{ textTransform: 'none', fontSize: '11px', alignSelf: 'flex-start' }}
          >
            Cancel
          </Button>
        </Box>
      );
    }

    const showOAuth =
      connectionMode === 'oauth' ||
      (connectionMode === null && (config?.authFields?.length ?? 0) === 0);
    const showCredentials =
      connectionMode === 'credentials' ||
      (connectionMode === null && (config?.authFields?.length ?? 0) > 0);

    return (
      <Box
        sx={{
          width: '100%',
          border: cardBorder,
          backgroundColor: cardBg,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <ProviderIconBadge
              IconComponent={IconComponent}
              brandColor={brandColor}
              isDark={isDark}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: titleColor,
                  lineHeight: 1.3,
                }}
              >
                New {providerName} Connection
              </Typography>
            </Box>
          </Box>

          {showOAuth && (
            <ConnectToProvider
              provider={provider}
              onSuccess={() => {
                fetchConnections(provider).then(() => {
                  const latest = getConnections(provider);
                  if (latest.length > 0) {
                    const newest = latest[0];
                    handleNewConnection(newest.id, newest.name);
                  }
                });
              }}
            >
              {({ onClick, isConnecting }) => (
                <Button
                  className="nodrag"
                  variant="contained"
                  size="small"
                  disabled={isConnecting}
                  onClick={onClick}
                  sx={{
                    width: '100%',
                    backgroundColor: brandColor,
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    '&:hover': {
                      filter: 'brightness(0.88)',
                      backgroundColor: brandColor,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isConnecting
                    ? 'Connecting...'
                    : `Connect to ${providerName}`}
                </Button>
              )}
            </ConnectToProvider>
          )}

          {showCredentials && (
            <CredentialsForm
              provider={provider}
              providerName={providerName}
              fields={config?.authFields ?? []}
              onSuccess={handleNewConnection}
            />
          )}

          <Button
            className="nodrag"
            size="small"
            onClick={() => setShowNewForm(false)}
            sx={{
              textTransform: 'none',
              fontSize: '11px',
              mt: 1,
              alignSelf: 'flex-start',
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  if (hasAnyConnection) {
    const selectedValue = selectedIntegrationId || '';
    const statusDotColor = selectedConnection ? '#22C55E' : '#98A2B3';
    const statusLabel = selectedConnection
      ? selectedConnection.isOwn
        ? 'Connected'
        : `Connected by ${selectedConnection.ownerUserName}`
      : 'Select a connection';

    return (
      <>
        <Box
          sx={{
            width: '100%',
            border: cardBorder,
            backgroundColor: cardBg,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <ProviderIconBadge
              IconComponent={IconComponent}
              brandColor={selectedConnection ? brandColor : '#98A2B3'}
              isDark={isDark}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: titleColor,
                  lineHeight: 1.3,
                }}
              >
                {providerName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.25,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: statusDotColor,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: subtitleColor,
                    lineHeight: 1,
                  }}
                >
                  {statusLabel}
                </Typography>
              </Box>
            </Box>

            {selectedConnection && isOwnConnection && (
              <IconButton
                className="nodrag"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={isDisconnecting}
                aria-label={`Disconnect ${providerName}`}
                sx={{
                  color: subtitleColor,
                  p: 0.5,
                  '&:hover': {
                    color: '#EF4444',
                    backgroundColor: isDark
                      ? 'rgba(239, 68, 68, 0.1)'
                      : 'rgba(239, 68, 68, 0.06)',
                  },
                }}
              >
                <X size={14} />
              </IconButton>
            )}
          </Box>

          <Box sx={{ px: 1.5, pb: 1.25 }}>
            <Select
              className="nodrag"
              size="small"
              value={selectedValue}
              onChange={(e) => handleSelectChange(e.target.value as string)}
              displayEmpty
              fullWidth
              renderValue={(val) => {
                if (!val) return 'Select a connection...';
                const c = connections.find((conn) => conn.id === val);
                if (!c) return 'Select a connection...';
                return `${c.name}${c.isOwn ? '' : ` (${c.ownerUserName})`}`;
              }}
              sx={{
                borderRadius: '6px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? '#475467' : '#D0D5DD',
                },
                bgcolor: isDark ? '#101828' : '#fff',
                color: isDark ? '#D0D5DD' : '#101828',
                fontSize: '12px',
              }}
            >
              {connections.map((conn) => (
                <MenuItem
                  key={conn.id}
                  value={conn.id}
                  sx={{
                    fontSize: '12px',
                    color: isDark ? '#D0D5DD' : '#101828',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: conn.isOwn ? '#22C55E' : '#6366F1',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: '12px', flex: 1, minWidth: 0 }}
                    >
                      {conn.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: subtitleColor,
                        flexShrink: 0,
                      }}
                    >
                      {conn.isOwn ? 'You' : conn.ownerUserName}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}

              <MenuItem
                value={NEW_CONNECTION_VALUE}
                sx={{
                  fontSize: '12px',
                  color: brandColor,
                  fontWeight: 600,
                  borderTop: `1px solid ${isDark ? '#475467' : '#EAECF0'}`,
                  mt: 0.5,
                  pt: 1,
                }}
              >
                <Plus size={14} style={{ marginRight: 6 }} />
                Add new connection
              </MenuItem>
            </Select>
          </Box>
        </Box>

        <Dialog
          open={confirmOpen}
          onClose={() => !isDisconnecting && setConfirmOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              bgcolor: isDark ? '#1D2939' : '#fff',
              color: titleColor,
              minWidth: 320,
            },
          }}
        >
          <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
            Disconnect {selectedConnection?.name ?? providerName}?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: subtitleColor, fontSize: '14px' }}>
              This connection will be removed. You can create a new one
              anytime.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={isDisconnecting}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              sx={{
                textTransform: 'none',
                backgroundColor: '#EF4444',
                '&:hover': { backgroundColor: '#DC2626' },
              }}
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (loadingMode) {
    return (
      <Box
        sx={{
          width: '100%',
          border: cardBorder,
          backgroundColor: cardBg,
          borderRadius: '8px',
          overflow: 'hidden',
          px: 1.5,
          py: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: '12px', color: subtitleColor }}>
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  const CustomCard = config?.ConnectionCard;
  if (CustomCard) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <CustomCard
          provider={provider}
          isConnected={false}
          onConnected={handleNewConnection}
        />
      </Box>
    );
  }

  const showOAuth =
    connectionMode === 'oauth' ||
    (connectionMode === null && (config?.authFields?.length ?? 0) === 0);
  const showCredentials =
    connectionMode === 'credentials' ||
    (connectionMode === null && (config?.authFields?.length ?? 0) > 0);
  const showWebhook = connectionMode === 'webhook';

  const disconnectedBrandColor = '#EF4444';

  return (
    <Box
      sx={{
        width: '100%',
        border: cardBorder,
        backgroundColor: cardBg,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: showCredentials || showOAuth || showWebhook ? 1.5 : 0,
          }}
        >
          <ProviderIconBadge
            IconComponent={IconComponent}
            brandColor={disconnectedBrandColor}
            isDark={isDark}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: titleColor,
                lineHeight: 1.3,
              }}
            >
              {providerName}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#EF4444',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: '11px', color: subtitleColor, lineHeight: 1 }}
              >
                Not connected
              </Typography>
            </Box>
          </Box>
        </Box>

        {showOAuth && (
          <ConnectToProvider
            provider={provider}
            onSuccess={() => {
              fetchConnections(provider).then(() => {
                const latest = getConnections(provider);
                if (latest.length > 0) {
                  const newest = latest[0];
                  handleNewConnection(newest.id, newest.name);
                }
              });
            }}
          >
            {({ onClick, isConnecting }) => (
              <Button
                className="nodrag"
                variant="contained"
                size="small"
                disabled={isConnecting}
                onClick={onClick}
                sx={{
                  width: '100%',
                  backgroundColor: brandColor,
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    filter: 'brightness(0.88)',
                    backgroundColor: brandColor,
                    boxShadow: 'none',
                  },
                }}
              >
                {isConnecting
                  ? 'Connecting...'
                  : `Connect to ${providerName}`}
              </Button>
            )}
          </ConnectToProvider>
        )}

        {showCredentials && (
          <CredentialsForm
            provider={provider}
            providerName={providerName}
            fields={config?.authFields ?? []}
            onSuccess={handleNewConnection}
          />
        )}

        {showWebhook && (
          <Button
            className="nodrag"
            variant="contained"
            size="small"
            disabled={isConnectingWebhook}
            onClick={handleWebhookConnect}
            sx={{
              width: '100%',
              backgroundColor: brandColor,
              color: '#fff',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                filter: 'brightness(0.88)',
                backgroundColor: brandColor,
                boxShadow: 'none',
              },
            }}
          >
            {isConnectingWebhook
              ? 'Connecting...'
              : `Connect to ${providerName}`}
          </Button>
        )}

        {connectionMode === null && !config && (
          <Typography sx={{ fontSize: '12px', color: '#F97066' }}>
            Provider not supported or unavailable.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ConnectionStatus;
